import { useTranslation } from "react-i18next";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { DatePicker } from "@/components/DatePicker";
import { t } from "i18next";
import { format, parse } from "date-fns";
import { Input } from "@/components/ui/input";
import { ScreenSizeSelect } from "../screenSize/ScreenSizeSelect";
import { useEffect } from "react";

interface Props {
  onFormValuesChange: (values: FormSchemaType) => void;
  onFormErrorsChange: (errors: any) => void;
}

const formSchema = z
  .object({
    name: z.string().min(1),
    startDate: z.string().min(1),
    endDate: z.string().min(1),
    screenSizeId: z.number().min(1),
    duration: z.number().min(1),
  })
  .refine(
    (data) => {
      if (!data.startDate || !data.endDate) return true;
      const start = parse(data.startDate, "dd-MM-yyyy HH:mm", new Date());
      const end = parse(data.endDate, "dd-MM-yyyy HH:mm", new Date());
      return end >= start;
    },
    {
      message: t("common.endDateMustBeAfterStartDate"),
      path: ["endDate"],
    }
  );

export type FormSchemaType = z.infer<typeof formSchema>;

export const CreateFromTemplateContentPropsForm: React.FC<Props> = ({ onFormValuesChange, onFormErrorsChange }) => {
  const { t } = useTranslation();

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    mode: "all",
    defaultValues: {
      startDate: format(new Date(), "dd-MM-yyyy HH:mm"),
      endDate: "",
      name: "",
      screenSizeId: undefined,
      duration: undefined,
    },
    reValidateMode: "onChange",
  });

  // Trigger validation when component mounts
  useEffect(() => {
    form.trigger();
    onFormErrorsChange(form.formState.errors);
  }, []);

  useEffect(() => {
    const subscription = form.watch((value) => {
      form.trigger();
      onFormValuesChange(value as FormSchemaType);
    });

    return () => subscription.unsubscribe();
  }, [form, onFormValuesChange]);

  useEffect(() => {
    onFormErrorsChange(form.formState.errors);
  }, [form.formState.errors, onFormErrorsChange]);

  return (
    <Form {...form}>
      <form className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("common.name")}</FormLabel>
              <FormControl>
                <div>
                  <Input value={field.value} onChange={field.onChange} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="duration"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("contentGroup.duration")}</FormLabel>
              <FormControl>
                <Input type="number" value={field.value} onChange={(e) => field.onChange(Number(e.target.value))} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="screenSizeId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("entities.screenSize")}</FormLabel>
              <FormControl>
                <ScreenSizeSelect field={field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("common.startDate")}</FormLabel>
                <FormControl>
                  <div>
                    <DatePicker value={field.value} onChange={field.onChange} withTime />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("common.endDate")}</FormLabel>
                <FormControl>
                  <div>
                    <DatePicker value={field.value} onChange={field.onChange} withTime />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
};
